/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * HOC для асинхронной загрузки Redux-saga
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.11.0
 */

import { ComponentType, FC, Fragment, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch, Reducer } from 'redux';
import { SagaIterator } from 'redux-saga';
import { createStructuredSelector } from 'reselect';
import { v4 } from 'uuid';

import { IAsyncReducer, IRootReducer } from '../../reducers/rootReducer';
import { asyncStateManagementInitAction, asyncStateManagementUnmountAction } from './asyncStateManagementActions';
import { IAsyncStateManagementReducer } from './asyncStateManagementReducer';
import { makeSelectAsyncStateManagementIsLoaded } from './asyncStateManagementSelectors';
import { IAsyncStateManagementInitPayload } from './asyncStateManagementTypes';

export interface IWithAsyncStateManagement {
    sagas?: () => SagaIterator;
    reducer?: { [K in keyof IAsyncReducer]?: Reducer<IAsyncReducer[K]> };
}

function withAsyncStateManagement<P>(
    WrapperComponent: ComponentType<P>,
    state: IWithAsyncStateManagement,
): FC<P> {
    type TComponentProps = P & IDispatchProps & IConnectedProps;
    const Component: FC<TComponentProps> = props => {

        const {
            isLoaded,
            // actions
            pageInit,
            pageUnmount,
        } = props;

        const [componentId, setComponentId] = useState('');

        useEffect(() => {
            const id = v4();
            pageInit({ id, sagas: state.sagas, reducer: state.reducer });
            setComponentId(id);
            return () => {
                pageUnmount(id);
            };
        }, []);

        return (
            <Fragment>
                {
                    isLoaded[componentId] && <WrapperComponent {...props} />
                }
            </Fragment>
        );
    };

    const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
        pageInit: (payload: IAsyncStateManagementInitPayload) => dispatch(asyncStateManagementInitAction(payload)),
        pageUnmount: (payload: string) => dispatch(asyncStateManagementUnmountAction(payload)),
    });

    const mapConnectedToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
        isLoaded: makeSelectAsyncStateManagementIsLoaded(),
    });

    // @ts-ignore
    return connect(mapConnectedToProps, mapDispatchToProps)(Component);
}

interface IDispatchProps {
    pageInit: (payload: IAsyncStateManagementInitPayload) => void;
    pageUnmount: (payload: string) => void;
}

interface IConnectedProps {
    isLoaded: IAsyncStateManagementReducer['isLoaded'];
}


export default withAsyncStateManagement;
